
import type {
  CompanyWithCounts,
  CreateCompanyBody,
  CreateUserBody,
  SeatWithUser,
  UpdateCompanyBody,
  UserWithCognitoStatus,
} from "@api/app-api";
import { CompanyTierEnum } from "@global/types/company";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import CalculateIcon from "@mui/icons-material/Calculate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockResetIcon from "@mui/icons-material/LockReset";
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import SecurityIcon from "@mui/icons-material/Security";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../api";
import { config, type Environment } from "../../config";
import { CompanyCreditsPanel } from "./company-credits-panel";
import { CompanySeatsPanel } from "./company-seats-panel";

// ============================================================================
// USER ROW COMPONENT
// ============================================================================

interface UserRowProps {
  user: UserWithCognitoStatus;
  onDisable: (userId: string, companyId: string) => Promise<void>;
  onEnable: (userId: string, companyId: string) => Promise<void>;
  onLiftAiSuspension: (userId: string, companyId: string) => Promise<void>;
  onDelete: (userId: string, companyId: string) => Promise<void>;
  onResetMfa: (userId: string, companyId: string) => Promise<void>;
  onResendTempPassword: (userId: string, companyId: string) => Promise<void>;
  isAdminUser: boolean;
  seatManagementEnabled: boolean;
}

const UserRow: React.FC<UserRowProps> = ({
  user,
  onDisable,
  onEnable,
  onLiftAiSuspension,
  onDelete,
  onResetMfa,
  onResendTempPassword,
  isAdminUser,
  seatManagementEnabled,
}) => {
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");

  // Get the expected confirmation text (first and last name)
  const expectedConfirmation =
    user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email;

  const handleDisable = async () => {
    setLoading(true);
    try {
      await onDisable(user.id, user.companyId);
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async () => {
    setLoading(true);
    try {
      await onEnable(user.id, user.companyId);
    } finally {
      setLoading(false);
    }
  };

  const handleLiftAiSuspension = async () => {
    setLoading(true);
    try {
      await onLiftAiSuspension(user.id, user.companyId);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setDeleteDialogOpen(false);
    setDeleteConfirmName("");
    try {
      await onDelete(user.id, user.companyId);
    } finally {
      setLoading(false);
    }
  };

  const handleResetMfa = async () => {
    setLoading(true);
    try {
      await onResetMfa(user.id, user.companyId);
    } finally {
      setLoading(false);
    }
  };

  const handleResendTempPassword = async () => {
    setLoading(true);
    setResendDialogOpen(false);
    try {
      await onResendTempPassword(user.id, user.companyId);
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = () => {
    const status = user.status;
    switch (status) {
      case "ACTIVE":
        return <Chip label="Active" color="success" size="small" />;
      case "INACTIVE":
        return <Chip label="Inactive" color="warning" size="small" />;
      case "DELETED":
        return <Chip label="Deleted" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const getCognitoStatusChip = () => {
    const status = user.cognitoStatus;
    switch (status) {
      case "CONFIRMED":
        return (
          <Chip
            label="Confirmed"
            size="small"
            variant="outlined"
            color="success"
          />
        );
      case "FORCE_CHANGE_PASSWORD":
        return (
          <Chip
            label="Password Reset"
            size="small"
            variant="outlined"
            color="warning"
          />
        );
      case "NOT_FOUND":
        return (
          <Chip
            label="Not in Cognito"
            size="small"
            variant="outlined"
            color="error"
          />
        );
      default:
        return (
          <Chip label={status || "Unknown"} size="small" variant="outlined" />
        );
    }
  };

  const formatLastUse = (lastUse?: string) => {
    if (!lastUse) return "Never";
    const date = new Date(lastUse);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const isDeleted = user.status === "DELETED";
  const canResendTempPassword = user.cognitoStatus === "FORCE_CHANGE_PASSWORD";

  return (
    <>
      <TableRow sx={{ opacity: isDeleted ? 0.5 : 1 }}>
        <TableCell>
          <Tooltip title={`User ID: ${user.id}`}>
            <Typography
              variant="caption"
              sx={{
                fontFamily: "monospace",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => navigator.clipboard.writeText(user.id)}
            >
              {user.id.slice(0, 8)}...
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2">
                {user.firstName || ""} {user.lastName || ""}
                {!user.firstName && !user.lastName && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    (No name)
                  </Typography>
                )}
              </Typography>
              {user.dataAligned ? (
                <Tooltip title="Cognito and database data match">
                  <CheckCircleIcon
                    fontSize="small"
                    sx={{ color: "success.main", fontSize: 16 }}
                  />
                </Tooltip>
              ) : (
                <Tooltip
                  title={
                    user.cognitoStatus === "NOT_FOUND"
                      ? "User not found in Cognito"
                      : `Data mismatch - DB email: ${user.email}, Cognito email: ${user.cognitoEmail || "N/A"} | DB companyId: ${user.companyId}, Cognito companyId: ${user.cognitoCompanyId || "N/A"}`
                  }
                >
                  <WarningIcon
                    fontSize="small"
                    sx={{ color: "warning.main", fontSize: 16 }}
                  />
                </Tooltip>
              )}
              {isAdminUser && (
                <Chip
                  label="Admin"
                  size="small"
                  color="primary"
                  sx={{ ml: 0.5 }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          {getStatusChip()}
          {user.abuseBlock && (
            <Tooltip
              title={`${user.abuseBlock.reason} · ${new Date(user.abuseBlock.at).toLocaleString()} · ${user.abuseBlock.detail}`}
            >
              <Chip
                label="AI suspended"
                color="error"
                size="small"
                variant="outlined"
                sx={{ ml: 0.5 }}
              />
            </Tooltip>
          )}
        </TableCell>
        <TableCell>{getCognitoStatusChip()}</TableCell>
        <TableCell>
          {user.mfaEnabled ? (
            <Chip label="MFA" size="small" color="success" variant="outlined" />
          ) : (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          )}
        </TableCell>
        {seatManagementEnabled && (
          <TableCell>
            {user.hasSeat ? (
              <Tooltip title={user.seatId || "Seat assigned"}>
                <Chip label="Has seat" size="small" color="success" />
              </Tooltip>
            ) : (
              <Chip label="No seat" size="small" variant="outlined" />
            )}
          </TableCell>
        )}
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {user.cognitoCreatedDate
              ? new Date(user.cognitoCreatedDate).toLocaleDateString()
              : "—"}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {formatLastUse(user.lastUse)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {user.termsAcceptedAt
              ? new Date(user.termsAcceptedAt).toLocaleDateString()
              : "—"}
          </Typography>
        </TableCell>
        <TableCell align="right">
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              {user.status === "ACTIVE" ? (
                <Tooltip title="Disable user">
                  <IconButton
                    size="small"
                    onClick={handleDisable}
                    disabled={isDeleted || isAdminUser}
                  >
                    <PersonOffIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : user.status === "INACTIVE" ? (
                <Tooltip title="Enable user">
                  <IconButton size="small" onClick={handleEnable}>
                    <PersonIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {user.abuseBlock && (
                <Tooltip title="Lift AI suspension">
                  <IconButton size="small" onClick={handleLiftAiSuspension}>
                    <LockOpenIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {user.mfaEnabled && (
                <Tooltip title="Reset MFA">
                  <IconButton
                    size="small"
                    onClick={handleResetMfa}
                    disabled={isDeleted}
                  >
                    <LockResetIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canResendTempPassword && (
                <Tooltip title="Resend temporary password email">
                  <IconButton
                    size="small"
                    onClick={() => setResendDialogOpen(true)}
                    disabled={isDeleted}
                  >
                    <ForwardToInboxIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip
                title={isAdminUser ? "Cannot delete admin user" : "Delete user"}
              >
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={isDeleted || isAdminUser}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </TableCell>
      </TableRow>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteConfirmName("");
        }}
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to <strong>permanently delete</strong>{" "}
            <strong>{user.email}</strong>? This will remove the user from
            Cognito and the database. This action cannot be undone.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            To confirm, please type the user's{" "}
            {user.firstName && user.lastName ? "full name" : "email"}:{" "}
            <strong>{expectedConfirmation}</strong>
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={user.firstName && user.lastName ? "Full name" : "Email"}
            fullWidth
            variant="outlined"
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteConfirmName("");
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteConfirmName !== expectedConfirmation}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resend Temporary Password Confirmation Dialog */}
      <Dialog
        open={resendDialogOpen}
        onClose={() => setResendDialogOpen(false)}
      >
        <DialogTitle>Resend Temporary Password</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Send a new temporary password email to <strong>{user.email}</strong>
            ? This will invalidate the previous temporary password.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResendDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResendTempPassword} variant="contained">
            Resend Email
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ============================================================================
// COMPANY CARD COMPONENT
// ============================================================================

interface CompanyCardProps {
  company: CompanyWithCounts;
  onCompanyChanged: (updates: Partial<CompanyWithCounts>) => void;
  onCompanyDeleted: () => void;
  seatManagementEnabled: boolean;
}

const CompanyCard: React.FC<CompanyCardProps> = ({
  company,
  onCompanyChanged,
  onCompanyDeleted,
  seatManagementEnabled,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [usersExpanded, setUsersExpanded] = useState(true);
  const [seatsExpanded, setSeatsExpanded] = useState(true);
  const [users, setUsers] = useState<UserWithCognitoStatus[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserFirstName, setNewUserFirstName] = useState("");
  const [newUserLastName, setNewUserLastName] = useState("");
  const [newUserAssignSeat, setNewUserAssignSeat] = useState(false);
  const [newUserSeatFlexible, setNewUserSeatFlexible] = useState(true);
  const [newUserSeatAutoReassign, setNewUserSeatAutoReassign] = useState(true);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addSeatsDialogOpen, setAddSeatsDialogOpen] = useState(false);
  const [recalcDialogOpen, setRecalcDialogOpen] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState<string | null>(null);

  // Edit form state
  const [editName, setEditName] = useState(company.name);
  const [editTier, setEditTier] = useState(company.tier);
  const [editMaxUserCount, setEditMaxUserCount] = useState(
    company.maxUserCount?.toString() ?? "",
  );
  const [editExpiryDate, setEditExpiryDate] = useState(company.expiryDate);
  const [editAdminUser, setEditAdminUser] = useState(company.adminUser);
  const [editMfaRequired, setEditMfaRequired] = useState(
    company.mfaRequired ?? false,
  );
  const [editAutoRenew, setEditAutoRenew] = useState(
    company.subscriptionAutoRenews ?? false,
  );
  const [editSubscriptionTerm, setEditSubscriptionTerm] = useState<
    "MONTH" | "YEAR"
  >(company.subscriptionTerm ?? "YEAR");
  const [editPricingMode, setEditPricingMode] = useState<"SEAT" | "CREDIT">(
    company.pricingMode ?? "SEAT",
  );
  const [editCreditsPerCase, setEditCreditsPerCase] = useState(
    company.creditsPerCase?.toString() ?? "",
  );
  const [editCreditsPerProjectMonth, setEditCreditsPerProjectMonth] = useState(
    company.creditsPerProjectMonth?.toString() ?? "",
  );
  const [editAiTrainingOptOut, setEditAiTrainingOptOut] = useState(
    company.aiTrainingOptOut ?? false,
  );

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const fetchedUsers = await apiClient.getCompanyUsers(company.id);
      setUsers(fetchedUsers);
    } catch (err) {
      console.error("Failed to load users:", err);
      setError("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  }, [company.id]);

  useEffect(() => {
    if (expanded && users.length === 0) {
      loadUsers();
    }
  }, [expanded, users.length, loadUsers]);

  // Reset edit form when dialog opens
  useEffect(() => {
    if (editDialogOpen) {
      setEditName(company.name);
      setEditTier(company.tier);
      setEditMaxUserCount(company.maxUserCount?.toString() ?? "");
      setEditExpiryDate(company.expiryDate);
      setEditAdminUser(company.adminUser);
      setEditMfaRequired(company.mfaRequired ?? false);
      setEditAutoRenew(company.subscriptionAutoRenews ?? false);
      setEditSubscriptionTerm(company.subscriptionTerm ?? "YEAR");
      setEditPricingMode(company.pricingMode ?? "SEAT");
      setEditCreditsPerCase(company.creditsPerCase?.toString() ?? "");
      setEditCreditsPerProjectMonth(
        company.creditsPerProjectMonth?.toString() ?? "",
      );
    }
  }, [editDialogOpen, company]);

  const resetCreateUserForm = () => {
    setNewUserEmail("");
    setNewUserFirstName("");
    setNewUserLastName("");
    setNewUserAssignSeat(false);
    setNewUserSeatFlexible(true);
    setNewUserSeatAutoReassign(true);
  };

  const handleCreateUser = async () => {
    if (!newUserEmail) return;
    setCreating(true);
    setError(null);
    const shouldCreateSeat = seatManagementEnabled && newUserAssignSeat;

    try {
      const userData: CreateUserBody = {
        email: newUserEmail,
        firstName: newUserFirstName || undefined,
        lastName: newUserLastName || undefined,
        seat: shouldCreateSeat
          ? {
              isFlexible: newUserSeatFlexible,
              autoReassign: newUserSeatFlexible
                ? newUserSeatAutoReassign
                : false,
            }
          : undefined,
      };
      const response = await apiClient.createUser(company.id, userData);
      setUsers((currentUsers) => [
        ...currentUsers,
        {
          id: response.userId,
          companyId: company.id,
          email: newUserEmail.toLowerCase(),
          firstName: newUserFirstName || undefined,
          lastName: newUserLastName || undefined,
          status: "ACTIVE",
          dataAligned: true,
          hasSeat: shouldCreateSeat,
        },
      ]);
      onCompanyChanged({
        activeUserCount: company.activeUserCount + 1,
        totalUserCount: company.totalUserCount + 1,
        seatCount: shouldCreateSeat ? company.seatCount + 1 : company.seatCount,
        assignedSeatCount: shouldCreateSeat
          ? company.assignedSeatCount + 1
          : company.assignedSeatCount,
      });
      setCreateDialogOpen(false);
      resetCreateUserForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateCompany = async () => {
    setSaving(true);
    setError(null);

    try {
      const updates: UpdateCompanyBody = {};
      const parsedMaxUserCount = Number.parseInt(editMaxUserCount, 10);

      if (editName !== company.name) updates.name = editName;
      if (editTier !== company.tier)
        updates.tier = editTier as
          | "BETA"
          | "TRIAL"
          | "INDIVIDUAL"
          | "TEAM"
          | "ENTERPRISE";
      if (editMaxUserCount.trim() === "") {
        if (company.maxUserCount !== null) updates.maxUserCount = null;
      } else if (
        Number.isFinite(parsedMaxUserCount) &&
        parsedMaxUserCount !== company.maxUserCount
      ) {
        updates.maxUserCount = parsedMaxUserCount;
      }
      if (editExpiryDate !== company.expiryDate)
        updates.expiryDate = editExpiryDate;
      if (editAdminUser !== company.adminUser)
        updates.adminUser = editAdminUser;

      if (editMfaRequired !== (company.mfaRequired ?? false))
        updates.mfaRequired = editMfaRequired;

      if (editAutoRenew !== (company.subscriptionAutoRenews ?? false))
        updates.subscriptionAutoRenews = editAutoRenew;

      if (editSubscriptionTerm !== (company.subscriptionTerm ?? "YEAR"))
        updates.subscriptionTerm = editSubscriptionTerm;

      if (editPricingMode !== (company.pricingMode ?? "SEAT"))
        updates.pricingMode = editPricingMode;

      const parsedCreditsPerCase = Number.parseInt(editCreditsPerCase, 10);
      if (
        editCreditsPerCase.trim() !== "" &&
        Number.isFinite(parsedCreditsPerCase) &&
        parsedCreditsPerCase !== company.creditsPerCase
      ) {
        updates.creditsPerCase = parsedCreditsPerCase;
      }

      const parsedCreditsPerMonth = Number.parseInt(
        editCreditsPerProjectMonth,
        10,
      );
      if (
        editCreditsPerProjectMonth.trim() !== "" &&
        Number.isFinite(parsedCreditsPerMonth) &&
        parsedCreditsPerMonth !== company.creditsPerProjectMonth
      ) {
        updates.creditsPerProjectMonth = parsedCreditsPerMonth;
      }

      if (editAiTrainingOptOut !== (company.aiTrainingOptOut ?? false))
        updates.aiTrainingOptOut = editAiTrainingOptOut;

      if (Object.keys(updates).length === 0) {
        setEditDialogOpen(false);
        return;
      }

      await apiClient.updateCompany(company.id, updates);
      setEditDialogOpen(false);
      onCompanyChanged(updates);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update company");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCompany = async () => {
    setDeleting(true);
    setError(null);

    try {
      await apiClient.deleteCompany(company.id);
      setDeleteDialogOpen(false);
      onCompanyDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete company");
      setDeleting(false);
    }
  };

  const handleRecalculateCompany = async () => {
    setRecalculating(true);
    setRecalcResult(null);
    setError(null);
    try {
      const result = await apiClient.recalculateCompany(company.id);
      setRecalcResult(result.message);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to trigger recalculation",
      );
    } finally {
      setRecalculating(false);
    }
  };

  const handleDisableUser = async (userId: string, companyId: string) => {
    const previousUsers = users;
    const user = users.find((candidate) => candidate.id === userId);
    setUsers((currentUsers) =>
      currentUsers.map((candidate) =>
        candidate.id === userId
          ? {
              ...candidate,
              status: "INACTIVE",
              hasSeat: false,
              seatId: undefined,
            }
          : candidate,
      ),
    );
    onCompanyChanged({
      activeUserCount: Math.max(0, company.activeUserCount - 1),
      assignedSeatCount:
        user?.hasSeat && company.assignedSeatCount > 0
          ? company.assignedSeatCount - 1
          : company.assignedSeatCount,
    });
    try {
      await apiClient.disableUser(userId, companyId);
    } catch (error) {
      setUsers(previousUsers);
      onCompanyChanged({
        activeUserCount: company.activeUserCount,
        assignedSeatCount: company.assignedSeatCount,
      });
      throw error;
    }
  };

  const handleEnableUser = async (userId: string, companyId: string) => {
    const previousUsers = users;
    setUsers((currentUsers) =>
      currentUsers.map((candidate) =>
        candidate.id === userId
          ? { ...candidate, status: "ACTIVE" }
          : candidate,
      ),
    );
    onCompanyChanged({ activeUserCount: company.activeUserCount + 1 });
    try {
      await apiClient.enableUser(userId, companyId);
    } catch (error) {
      setUsers(previousUsers);
      onCompanyChanged({ activeUserCount: company.activeUserCount });
      throw error;
    }
  };

  const handleLiftAiSuspension = async (userId: string, companyId: string) => {
    const previousUsers = users;
    setUsers((currentUsers) =>
      currentUsers.map((candidate) =>
        candidate.id === userId
          ? { ...candidate, abuseBlock: undefined }
          : candidate,
      ),
    );
    try {
      await apiClient.liftAiSuspension(userId, companyId);
    } catch (error) {
      setUsers(previousUsers);
      throw error;
    }
  };

  const handleDeleteUser = async (userId: string, companyId: string) => {
    const previousUsers = users;
    const user = users.find((candidate) => candidate.id === userId);
    setUsers((currentUsers) =>
      currentUsers.map((candidate) =>
        candidate.id === userId
          ? { ...candidate, status: "DELETED" }
          : candidate,
      ),
    );
    onCompanyChanged({
      activeUserCount:
        user?.status === "ACTIVE"
          ? Math.max(0, company.activeUserCount - 1)
          : company.activeUserCount,
    });
    try {
      await apiClient.deleteUser(userId, companyId);
    } catch (error) {
      setUsers(previousUsers);
      onCompanyChanged({ activeUserCount: company.activeUserCount });
      throw error;
    }
  };

  const handleResetMfa = async (userId: string, companyId: string) => {
    const previousUsers = users;
    setUsers((currentUsers) =>
      currentUsers.map((candidate) =>
        candidate.id === userId
          ? { ...candidate, mfaEnabled: false }
          : candidate,
      ),
    );
    try {
      await apiClient.resetMfa(userId, companyId);
    } catch (error) {
      setUsers(previousUsers);
      throw error;
    }
  };

  const handleResendTempPassword = async (
    userId: string,
    companyId: string,
  ) => {
    await apiClient.resendTempPassword(userId, companyId);
  };

  const handleSeatsChanged = (seats: SeatWithUser[]) => {
    setUsers((currentUsers) =>
      currentUsers.map((user) => {
        const seat = seats.find(
          (candidate) => candidate.assignedUserId === user.id,
        );
        return { ...user, hasSeat: !!seat, seatId: seat?.id };
      }),
    );
    onCompanyChanged({
      seatCount: seats.length,
      assignedSeatCount: seats.filter((seat) => !!seat.assignedUserId).length,
    });
  };

  const getTierChip = () => {
    const colors: Record<
      string,
      "default" | "primary" | "secondary" | "success" | "warning" | "error"
    > = {
      BETA: "secondary",
      TRIAL: "warning",
      INDIVIDUAL: "default",
      TEAM: "primary",
      ENTERPRISE: "success",
    };
    return (
      <Chip
        label={company.tier}
        size="small"
        color={colors[company.tier] || "default"}
      />
    );
  };

  const expiryDate = new Date(company.expiryDate);
  const now = new Date();
  const isExpired = expiryDate < now;
  const needsInvoice = isExpired && (company.subscriptionAutoRenews ?? false);
  const needsDeletion =
    isExpired &&
    now.getTime() - expiryDate.getTime() > 30 * 24 * 60 * 60 * 1000;

  // Get active users for admin selection
  const activeUsers = users.filter((u) => u.status === "ACTIVE");
  const userLimitReached =
    company.maxUserCount !== null &&
    company.activeUserCount >= company.maxUserCount;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ pb: expanded ? 0 : undefined }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              cursor: "pointer",
              flex: 1,
            }}
            onClick={() => setExpanded(!expanded)}
          >
            <BusinessIcon color="action" />
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6">{company.name}</Typography>
                <Tooltip title={`Company ID: ${company.id}`}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "monospace",
                      color: "text.secondary",
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(company.id);
                    }}
                  >
                    ({company.id.slice(0, 8)}...)
                  </Typography>
                </Tooltip>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {company.adminUserEmail || company.adminUser}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {getTierChip()}
            {company.mfaRequired && (
              <Tooltip title="MFA enforcement enabled">
                <Chip
                  icon={<SecurityIcon />}
                  label="MFA"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </Tooltip>
            )}
            {company.aiTrainingOptOut && (
              <Tooltip title="Opted out of quality analysis">
                <Chip
                  label="No quality analysis"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Tooltip>
            )}
            <Chip
              label={`${company.activeUserCount} active / ${company.totalUserCount} total / ${company.maxUserCount === null ? "unlimited" : company.maxUserCount} allowed users`}
              size="small"
              variant="outlined"
              color={userLimitReached ? "error" : "default"}
            />
            {seatManagementEnabled && (
              <Chip
                label={`${company.assignedSeatCount}/${company.seatCount} seats`}
                size="small"
                variant="outlined"
                color={
                  company.seatCount > 0 &&
                  company.assignedSeatCount >= company.seatCount
                    ? "warning"
                    : "default"
                }
              />
            )}
            {isExpired && !needsInvoice && (
              <Chip label="Expired" size="small" color="error" />
            )}
            {needsInvoice && (
              <Chip label="Needs Invoice" size="small" color="warning" />
            )}
            {needsDeletion && (
              <Chip
                label="Needs Deletion"
                size="small"
                color="error"
                variant="outlined"
              />
            )}
            <Typography variant="body2" color="text.secondary">
              Expires: {new Date(company.expiryDate).toLocaleDateString()}
            </Typography>
            <Tooltip title="Recalculate all projects">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setRecalcResult(null);
                  setRecalcDialogOpen(true);
                }}
              >
                <CalculateIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit company">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditDialogOpen(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete company">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteDialogOpen(true);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>
      </CardContent>

      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          {loadingUsers && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mb: 2,
            }}
          >
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadUsers}
              disabled={loadingUsers}
            >
              Refresh Users
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              disabled={userLimitReached}
            >
              Add User
            </Button>
            {seatManagementEnabled && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddSeatsDialogOpen(true)}
              >
                Add Seats
              </Button>
            )}
          </Box>

          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 1.5,
                cursor: "pointer",
              }}
              onClick={() => setUsersExpanded(!usersExpanded)}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Users ({users.length})
              </Typography>
              <IconButton size="small">
                {usersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={usersExpanded}>
              {seatManagementEnabled && (
                <Alert severity="info" variant="outlined" sx={{ mx: 2, mb: 2 }}>
                  Users can exist without a seat, but they need a seat to use
                  Bayescase. Companies can have unlimited users when Max Users
                  is blank.
                </Alert>
              )}
              {users.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 100 }}>ID</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Cognito</TableCell>
                        <TableCell>MFA</TableCell>
                        {seatManagementEnabled && <TableCell>Seat</TableCell>}
                        <TableCell>Created</TableCell>
                        <TableCell>Last Use</TableCell>
                        <TableCell>Terms</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user) => (
                        <UserRow
                          key={user.id}
                          user={user}
                          onDisable={handleDisableUser}
                          onEnable={handleEnableUser}
                          onLiftAiSuspension={handleLiftAiSuspension}
                          onDelete={handleDeleteUser}
                          onResetMfa={handleResetMfa}
                          onResendTempPassword={handleResendTempPassword}
                          isAdminUser={user.id === company.adminUser}
                          seatManagementEnabled={seatManagementEnabled}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                !loadingUsers && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ py: 2 }}
                  >
                    No users found
                  </Typography>
                )
              )}
            </Collapse>
          </Paper>

          {/* Credit-based companies don't use seats; show the credits panel
              instead of the seats panel. */}
          {company.pricingMode === "CREDIT" ? (
            <Paper variant="outlined">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                }}
                onClick={() => setSeatsExpanded(!seatsExpanded)}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Credits
                </Typography>
                <IconButton size="small">
                  {seatsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={seatsExpanded}>
                <Box sx={{ p: 2 }}>
                  <CompanyCreditsPanel companyId={company.id} />
                </Box>
              </Collapse>
            </Paper>
          ) : (
            seatManagementEnabled && (
              <Paper variant="outlined">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                  }}
                  onClick={() => setSeatsExpanded(!seatsExpanded)}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    Seats ({company.assignedSeatCount}/{company.seatCount}{" "}
                    assigned)
                  </Typography>
                  <IconButton size="small">
                    {seatsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={seatsExpanded}>
                  <Box sx={{ p: 2 }}>
                    <CompanySeatsPanel
                      companyId={company.id}
                      users={users}
                      onSeatsChanged={handleSeatsChanged}
                      addDialogOpen={addSeatsDialogOpen}
                      onAddDialogOpenChange={setAddSeatsDialogOpen}
                      hideHeaderActions
                    />
                  </Box>
                </Collapse>
              </Paper>
            )
          )}
        </Box>
      </Collapse>

      {/* Create User Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          resetCreateUserForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add User to {company.name}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              margin="dense"
              label="First Name (optional)"
              fullWidth
              variant="outlined"
              value={newUserFirstName}
              onChange={(e) => setNewUserFirstName(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Last Name (optional)"
              fullWidth
              variant="outlined"
              value={newUserLastName}
              onChange={(e) => setNewUserLastName(e.target.value)}
            />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: "block" }}
          >
            The user will receive an email with a temporary password.
          </Typography>
          {seatManagementEnabled && (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newUserAssignSeat}
                    onChange={(_, checked) => setNewUserAssignSeat(checked)}
                  />
                }
                label="Create and assign seat"
              />
              {newUserAssignSeat && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel>Seat type</InputLabel>
                    <Select
                      value={newUserSeatFlexible ? "flexible" : "named"}
                      label="Seat type"
                      onChange={(event) => {
                        const isFlexible = event.target.value === "flexible";
                        setNewUserSeatFlexible(isFlexible);
                        if (!isFlexible) setNewUserSeatAutoReassign(false);
                      }}
                    >
                      <MenuItem value="flexible">Flexible</MenuItem>
                      <MenuItem value="named">Named</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newUserSeatFlexible && newUserSeatAutoReassign}
                        disabled={!newUserSeatFlexible}
                        onChange={(_, checked) =>
                          setNewUserSeatAutoReassign(checked)
                        }
                      />
                    }
                    label="Auto reassign"
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              resetCreateUserForm();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={!newUserEmail || creating}
          >
            {creating ? <CircularProgress size={24} /> : "Create User"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Company</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Company Name"
            fullWidth
            variant="outlined"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Tier</InputLabel>
            <Select
              value={editTier}
              onChange={(e) => setEditTier(e.target.value)}
              label="Tier"
            >
              <MenuItem value="BETA">Beta</MenuItem>
              <MenuItem value="TRIAL">Trial</MenuItem>
              <MenuItem value="INDIVIDUAL">Individual</MenuItem>
              <MenuItem value="TEAM">Team</MenuItem>
              <MenuItem value="ENTERPRISE">Enterprise</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Max Users"
              type="number"
              fullWidth
              variant="outlined"
              value={editMaxUserCount}
              onChange={(e) => setEditMaxUserCount(e.target.value)}
              inputProps={{ min: 1 }}
              helperText="Leave empty for unlimited users"
            />
          </Box>
          <TextField
            margin="dense"
            label="Expiry Date"
            type="date"
            fullWidth
            variant="outlined"
            value={editExpiryDate.split("T")[0]}
            onChange={(e) => setEditExpiryDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          {activeUsers.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Admin User</InputLabel>
              <Select
                value={editAdminUser}
                onChange={(e) => setEditAdminUser(e.target.value)}
                label="Admin User"
              >
                {activeUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.email}{" "}
                    {user.firstName && `(${user.firstName} ${user.lastName})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={editMfaRequired}
                onChange={(e) => setEditMfaRequired(e.target.checked)}
              />
            }
            label="Enforce MFA for all users"
            sx={{ mb: 2, display: "block" }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editAutoRenew}
                onChange={(e) => setEditAutoRenew(e.target.checked)}
              />
            }
            label="Subscription auto-renews"
            sx={{ mb: 2, display: "block" }}
          />
          {editAutoRenew && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Subscription Term</InputLabel>
              <Select
                value={editSubscriptionTerm}
                onChange={(e) =>
                  setEditSubscriptionTerm(e.target.value as "MONTH" | "YEAR")
                }
                label="Subscription Term"
              >
                <MenuItem value="MONTH">Monthly</MenuItem>
                <MenuItem value="YEAR">Yearly</MenuItem>
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Pricing Mode</InputLabel>
            <Select
              value={editPricingMode}
              onChange={(e) =>
                setEditPricingMode(e.target.value as "SEAT" | "CREDIT")
              }
              label="Pricing Mode"
            >
              <MenuItem value="SEAT">Seat-based</MenuItem>
              <MenuItem value="CREDIT">Credit-based</MenuItem>
            </Select>
          </FormControl>
          {editPricingMode === "CREDIT" && (
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Credits per case"
                type="number"
                value={editCreditsPerCase}
                onChange={(e) => setEditCreditsPerCase(e.target.value)}
                placeholder="5"
                inputProps={{ min: 1 }}
                helperText="Default 5"
              />
              <TextField
                fullWidth
                label="Credits per project / month"
                type="number"
                value={editCreditsPerProjectMonth}
                onChange={(e) =>
                  setEditCreditsPerProjectMonth(e.target.value)
                }
                placeholder="1"
                inputProps={{ min: 1 }}
                helperText="Default 1"
              />
            </Box>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={!editAiTrainingOptOut}
                onChange={(e) => setEditAiTrainingOptOut(!e.target.checked)}
              />
            }
            label="Cases may be used for quality analysis"
            sx={{ mb: 2, display: "block" }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateCompany}
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recalculate Company Dialog */}
      <Dialog
        open={recalcDialogOpen}
        onClose={() => setRecalcDialogOpen(false)}
      >
        <DialogTitle>Recalculate All Projects</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Trigger a full recalculation of <strong>every</strong> project and
            portfolio of <strong>{company.name}</strong>? Closed projects are
            skipped. For large companies this starts many calculation jobs and
            can take a while to complete.
          </DialogContentText>
          {recalcResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {recalcResult}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecalcDialogOpen(false)}>
            {recalcResult ? "Close" : "Cancel"}
          </Button>
          <Button
            onClick={handleRecalculateCompany}
            variant="contained"
            disabled={recalculating || !!recalcResult}
          >
            {recalculating ? <CircularProgress size={24} /> : "Recalculate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Company Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteConfirmName("");
        }}
      >
        <DialogTitle>Delete Company</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{company.name}</strong>?
            This will <strong>permanently delete</strong> all{" "}
            {company.totalUserCount} users and remove the company. This action
            cannot be undone.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            To confirm, please type the company name:{" "}
            <strong>{company.name}</strong>
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Company name"
            fullWidth
            variant="outlined"
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
            sx={{ mt: 2 }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteConfirmName("");
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCompany}
            color="error"
            variant="contained"
            disabled={deleting || deleteConfirmName !== company.name}
          >
            {deleting ? <CircularProgress size={24} /> : "Delete Company"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

// ============================================================================
// COMPANIES PAGE COMPONENT
// ============================================================================

interface CompaniesPageProps {
  environment: Environment;
}

export const CompaniesPage: React.FC<CompaniesPageProps> = ({
  environment,
}) => {
  const seatManagementEnabled = config.supportsSeatManagement(environment);
  const [companies, setCompanies] = useState<CompanyWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  // Create company form state
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newCompanyTier, setNewCompanyTier] = useState("TRIAL");
  const [newCompanyMaxUsers, setNewCompanyMaxUsers] = useState("");
  const [newCompanyExpiry, setNewCompanyExpiry] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    return date.toISOString().split("T")[0];
  });
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminFirstName, setNewAdminFirstName] = useState("");
  const [newAdminLastName, setNewAdminLastName] = useState("");

  // Billing model + initial provisioning
  const [newCompanyPricingMode, setNewCompanyPricingMode] = useState<
    "SEAT" | "CREDIT"
  >("SEAT");
  // Seat mode
  const [newCompanySeatCount, setNewCompanySeatCount] = useState("");
  const [newCompanySeatsFlexible, setNewCompanySeatsFlexible] = useState(false);
  // Credit mode
  const [newCompanyCreditsPerCase, setNewCompanyCreditsPerCase] = useState("");
  const [newCompanyCreditsPerProjectMonth, setNewCompanyCreditsPerProjectMonth] =
    useState("");
  const [newCompanyInitialCredits, setNewCompanyInitialCredits] = useState("");
  const [newCompanyCreditsNote, setNewCompanyCreditsNote] = useState("");
  const [newCompanyAiTrainingOptOut, setNewCompanyAiTrainingOptOut] =
    useState(false);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedCompanies = await apiClient.getCompanies();
      setCompanies(fetchedCompanies);
    } catch (err) {
      console.error("Failed to load companies:", err);
      setError(err instanceof Error ? err.message : "Failed to load companies");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies, environment]);

  const handleCreateCompany = async () => {
    if (!newCompanyName || !newAdminEmail) return;
    setCreating(true);
    setError(null);

    try {
      const isCreditMode = newCompanyPricingMode === "CREDIT";
      const seatCount = parseInt(newCompanySeatCount, 10);
      const initialCredits = parseInt(newCompanyInitialCredits, 10);
      const creditsPerCase = parseInt(newCompanyCreditsPerCase, 10);
      const creditsPerProjectMonth = parseInt(
        newCompanyCreditsPerProjectMonth,
        10,
      );

      const companyData: CreateCompanyBody = {
        name: newCompanyName,
        tier: newCompanyTier as CompanyTierEnum,
        maxUserCount:
          newCompanyMaxUsers.trim() === ""
            ? null
            : parseInt(newCompanyMaxUsers, 10),
        expiryDate: newCompanyExpiry,
        adminEmail: newAdminEmail,
        adminFirstName: newAdminFirstName || undefined,
        adminLastName: newAdminLastName || undefined,
        pricingMode: newCompanyPricingMode,
        creditsPerCase:
          isCreditMode && Number.isFinite(creditsPerCase)
            ? creditsPerCase
            : undefined,
        creditsPerProjectMonth:
          isCreditMode && Number.isFinite(creditsPerProjectMonth)
            ? creditsPerProjectMonth
            : undefined,
        initialSeats:
          !isCreditMode &&
          seatManagementEnabled &&
          Number.isFinite(seatCount) &&
          seatCount > 0
            ? { count: seatCount, isFlexible: newCompanySeatsFlexible }
            : undefined,
        initialCredits:
          isCreditMode && Number.isFinite(initialCredits) && initialCredits > 0
            ? {
                credits: initialCredits,
                note: newCompanyCreditsNote || undefined,
              }
            : undefined,
        aiTrainingOptOut: newCompanyAiTrainingOptOut,
      };
      const response = await apiClient.createCompany(companyData);
      const createdCompany: CompanyWithCounts = {
        id: response.companyId,
        name: newCompanyName,
        tier: newCompanyTier,
        expiryDate: newCompanyExpiry,
        createdDate: new Date().toISOString(),
        adminUser: response.adminUserId,
        adminUserEmail: newAdminEmail.toLowerCase(),
        maxUserCount: companyData.maxUserCount,
        activeUserCount: 1,
        totalUserCount: 1,
        seatCount: response.seatsCreated,
        assignedSeatCount: 0,
        mfaRequired: false,
        subscriptionAutoRenews: false,
        subscriptionTerm: "YEAR",
        pricingMode: newCompanyPricingMode,
        creditsPerCase: companyData.creditsPerCase,
        creditsPerProjectMonth: companyData.creditsPerProjectMonth,
        availableCredits: isCreditMode ? response.creditsGranted : undefined,
        aiTrainingOptOut: newCompanyAiTrainingOptOut,
      };
      setCompanies((currentCompanies) =>
        [...currentCompanies, createdCompany].sort((a, b) =>
          a.name.localeCompare(b.name),
        ),
      );
      setCreateDialogOpen(false);
      resetCreateForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create company");
    } finally {
      setCreating(false);
    }
  };

  const resetCreateForm = () => {
    setNewCompanyName("");
    setNewCompanyTier("TRIAL");
    setNewCompanyMaxUsers("");
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    setNewCompanyExpiry(date.toISOString().split("T")[0]);
    setNewAdminEmail("");
    setNewAdminFirstName("");
    setNewAdminLastName("");
    setNewCompanyPricingMode("SEAT");
    setNewCompanySeatCount("");
    setNewCompanySeatsFlexible(false);
    setNewCompanyCreditsPerCase("");
    setNewCompanyCreditsPerProjectMonth("");
    setNewCompanyInitialCredits("");
    setNewCompanyCreditsNote("");
    setNewCompanyAiTrainingOptOut(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5">Companies ({companies.length})</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadCompanies}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Company
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {companies.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No companies found</Typography>
        </Paper>
      ) : (
        companies.map((company) => (
          <CompanyCard
            key={company.id}
            company={company}
            onCompanyChanged={(updates) =>
              setCompanies((currentCompanies) =>
                currentCompanies.map((candidate) =>
                  candidate.id === company.id
                    ? { ...candidate, ...updates }
                    : candidate,
                ),
              )
            }
            onCompanyDeleted={() =>
              setCompanies((currentCompanies) =>
                currentCompanies.filter(
                  (candidate) => candidate.id !== company.id,
                ),
              )
            }
            seatManagementEnabled={seatManagementEnabled}
          />
        ))
      )}

      {/* Create Company Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          resetCreateForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Company</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
            Company Details
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Company Name"
            fullWidth
            variant="outlined"
            value={newCompanyName}
            onChange={(e) => setNewCompanyName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Tier</InputLabel>
            <Select
              value={newCompanyTier}
              onChange={(e) => setNewCompanyTier(e.target.value)}
              label="Tier"
            >
              <MenuItem value="BETA">Beta</MenuItem>
              <MenuItem value="TRIAL">Trial</MenuItem>
              <MenuItem value="INDIVIDUAL">Individual</MenuItem>
              <MenuItem value="TEAM">Team</MenuItem>
              <MenuItem value="ENTERPRISE">Enterprise</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Max Users"
              type="number"
              fullWidth
              variant="outlined"
              value={newCompanyMaxUsers}
              onChange={(e) => setNewCompanyMaxUsers(e.target.value)}
              inputProps={{ min: 1 }}
              helperText="Leave empty for unlimited users"
            />
          </Box>
          <TextField
            margin="dense"
            label="Expiry Date"
            type="date"
            fullWidth
            variant="outlined"
            value={newCompanyExpiry}
            onChange={(e) => setNewCompanyExpiry(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Billing & Provisioning
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Pricing Mode</InputLabel>
            <Select
              value={newCompanyPricingMode}
              onChange={(e) =>
                setNewCompanyPricingMode(e.target.value as "SEAT" | "CREDIT")
              }
              label="Pricing Mode"
            >
              <MenuItem value="SEAT">Seat-based</MenuItem>
              <MenuItem value="CREDIT">Credit-based</MenuItem>
            </Select>
          </FormControl>

          {newCompanyPricingMode === "SEAT" ? (
            seatManagementEnabled ? (
              <Box sx={{ mb: 2 }}>
                <TextField
                  margin="dense"
                  label="Initial Seats"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={newCompanySeatCount}
                  onChange={(e) => setNewCompanySeatCount(e.target.value)}
                  inputProps={{ min: 1, max: 100 }}
                  helperText="Leave empty to create no seats up front"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={newCompanySeatsFlexible}
                      onChange={(e) =>
                        setNewCompanySeatsFlexible(e.target.checked)
                      }
                    />
                  }
                  label="Flexible seats"
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                Seat management is not available in this environment, so no seats
                can be created up front.
              </Alert>
            )
          ) : (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Credits per case"
                  type="number"
                  value={newCompanyCreditsPerCase}
                  onChange={(e) => setNewCompanyCreditsPerCase(e.target.value)}
                  placeholder="5"
                  inputProps={{ min: 1 }}
                  helperText="Default 5"
                />
                <TextField
                  fullWidth
                  label="Credits per project / month"
                  type="number"
                  value={newCompanyCreditsPerProjectMonth}
                  onChange={(e) =>
                    setNewCompanyCreditsPerProjectMonth(e.target.value)
                  }
                  placeholder="1"
                  inputProps={{ min: 1 }}
                  helperText="Default 1"
                />
              </Box>
              <TextField
                margin="dense"
                label="Initial Credits"
                type="number"
                fullWidth
                variant="outlined"
                value={newCompanyInitialCredits}
                onChange={(e) => setNewCompanyInitialCredits(e.target.value)}
                inputProps={{ min: 1 }}
                helperText="Credits granted up front (expire in 2 years). Leave empty to grant none."
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="Allocation note (optional)"
                fullWidth
                variant="outlined"
                value={newCompanyCreditsNote}
                onChange={(e) => setNewCompanyCreditsNote(e.target.value)}
                inputProps={{ maxLength: 500 }}
              />
            </Box>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={!newCompanyAiTrainingOptOut}
                onChange={(e) =>
                  setNewCompanyAiTrainingOptOut(!e.target.checked)
                }
              />
            }
            label="Cases may be used for quality analysis"
            sx={{ mb: 3, display: "block" }}
          />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Admin User (will receive login email)
          </Typography>
          <TextField
            margin="dense"
            label="Admin Email"
            type="email"
            fullWidth
            variant="outlined"
            value={newAdminEmail}
            onChange={(e) => setNewAdminEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              margin="dense"
              label="First Name (optional)"
              fullWidth
              variant="outlined"
              value={newAdminFirstName}
              onChange={(e) => setNewAdminFirstName(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Last Name (optional)"
              fullWidth
              variant="outlined"
              value={newAdminLastName}
              onChange={(e) => setNewAdminLastName(e.target.value)}
            />
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              resetCreateForm();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCompany}
            variant="contained"
            disabled={!newCompanyName || !newAdminEmail || creating}
          >
            {creating ? <CircularProgress size={24} /> : "Create Company"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
